import {
  type HomeScreenLayout,
  type IconItem,
  type FormFactor,
  SPECS,
  MAX_HOME_PAGES,
  MAX_FOLDER_PAGES,
  pageCapacity,
  folderPageCapacity,
} from "./types";
import { readExtension, getVariant } from "./projection";

export interface ValidationIssue {
  level: "error" | "warning";
  message: string;
}

export function validateLayout(layout: HomeScreenLayout, ff: FormFactor): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const spec = SPECS[ff];

  if (!Array.isArray(layout.Pages) || layout.Pages.length === 0) {
    issues.push({ level: "error", message: `${spec.label}: at least one page is required.` });
    return issues;
  }
  if (layout.Pages.length > MAX_HOME_PAGES) {
    issues.push({
      level: "error",
      message: `${spec.label}: maximum ${MAX_HOME_PAGES} pages allowed.`,
    });
  }
  if (layout.Pages.every((p) => p.length === 0)) {
    issues.push({ level: "error", message: `${spec.label}: place at least one icon.` });
  }

  const cap = pageCapacity(ff);
  layout.Pages.forEach((page, i) => {
    if (page.length > cap) {
      issues.push({
        level: "error",
        message: `${spec.label} page ${i + 1}: exceeds ${cap}-icon grid.`,
      });
    }
    page.forEach((it) => collectItemIssues(it, ff, issues));
  });

  if (layout.Dock && layout.Dock.length > spec.dockMax) {
    issues.push({
      level: "error",
      message: `${spec.label}: dock supports up to ${spec.dockMax} items.`,
    });
  }
  layout.Dock?.forEach((it) => collectItemIssues(it, ff, issues));

  return issues;
}

// Reverse-DNS bundle identifier (e.g. com.apple.mobilesafari).
const BUNDLE_ID_RE = /^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;

function collectItemIssues(item: IconItem, ff: FormFactor, issues: ValidationIssue[]) {
  if (item.Type === "WebClip") {
    const label = item.DisplayName || item.URL || "Web clip";
    if (!item.URL || !item.URL.trim()) {
      issues.push({ level: "error", message: `Web clip "${label}" is missing a URL.` });
    } else if (!/^https?:\/\/\S+$/i.test(item.URL.trim())) {
      issues.push({ level: "error", message: `Web clip "${label}" has an invalid URL (must start with http(s)://).` });
    }
  } else if (item.Type === "Application") {
    const label = item.DisplayName || item.BundleID || "Application";
    if (!item.BundleID || !item.BundleID.trim()) {
      issues.push({ level: "error", message: `Application "${label}" is missing a bundle ID.` });
    } else if (!BUNDLE_ID_RE.test(item.BundleID.trim())) {
      issues.push({
        level: "error",
        message: `Application "${label}" has an invalid bundle ID "${item.BundleID}" (expected reverse-DNS, e.g. com.example.app).`,
      });
    }
  } else if (item.Type === "Folder") {
    if (!item.DisplayName?.trim()) {
      issues.push({ level: "warning", message: "Folder is missing a name." });
    }
    if (!item.Pages?.length || item.Pages.every((p) => p.length === 0)) {
      issues.push({ level: "warning", message: `Folder "${item.DisplayName}" is empty and will be skipped on device.` });
    }
    if (item.Pages?.length > MAX_FOLDER_PAGES) {
      issues.push({
        level: "error",
        message: `Folder "${item.DisplayName}": exceeds ${MAX_FOLDER_PAGES} pages.`,
      });
    }
    const fcap = folderPageCapacity(ff);
    item.Pages?.forEach((p, i) => {
      if (p.length > fcap) {
        issues.push({
          level: "error",
          message: `Folder "${item.DisplayName}" page ${i + 1}: exceeds ${fcap}-icon grid.`,
        });
      }
      p.forEach((nested) => collectItemIssues(nested, ff, issues));
    });
  }
}

export function summarize(issues: ValidationIssue[]) {
  return {
    errors: issues.filter((i) => i.level === "error").length,
    warnings: issues.filter((i) => i.level === "warning").length,
  };
}

/**
 * Validate a full HomeScreenConfig (the value persisted via onChange).
 * Considers the iPhone and/or iPad variants based on the x-smplify target.
 */
export function validateHomeScreenConfig(config: Record<string, unknown> | null | undefined): ValidationIssue[] {
  if (!config || typeof config !== "object") {
    return [{ level: "error", message: "Home screen layout is missing." }];
  }
  // readExtension/getVariant are statically imported above.
  const ext = readExtension(config as never);
  const out: ValidationIssue[] = [];
  if (ext.target === "iphone" || ext.target === "both") {
    out.push(...validateLayout(getVariant(ext, "iphone"), "iphone"));
  }
  if (ext.target === "ipad" || ext.target === "both") {
    out.push(...validateLayout(getVariant(ext, "ipad"), "ipad"));
  }
  return out;
}
